import { useCallback, useMemo, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';
import { Branch } from './useBranches';
import { useCachedData } from './useCache';
import { CacheKeys, UserContext } from '@/stores/cacheKeys';
import { TTL_CONFIG } from '@/stores/cacheConfig';
import { useNetwork } from '@/contexts/NetworkContext';
import { useElectron } from '@/hooks/useElectron';

export const useFilteredBranches = () => {
  const { user, isStaff, staffSession } = useAuth();
  const { isOnline, isElectronApp } = useNetwork();
  const { database } = useElectron();
  
  // Determine if we should skip network fetch
  const shouldUseOffline = isElectronApp && !isOnline && !!database;
  
  // State for offline branches
  const [offlineBranches, setOfflineBranches] = useState<Branch[]>([]);
  const [offlineLoading, setOfflineLoading] = useState(false);

  // Create user context for caching (memoized to prevent dependency issues)
  const userContext: UserContext | null = useMemo(() => {
    return user || staffSession ? {
      userId: user?.id || staffSession?.id || '',
      restaurantId: staffSession?.restaurant_id || '',
      isStaff: isStaff,
      allowedBranches: staffSession?.allowed_branches || []
    } : null;
  }, [user, isStaff, staffSession]);

  // Generate cache key
  const cacheKey = userContext ? CacheKeys.branches(userContext) : '';

  // Define the fetcher function
  const fetchBranches = useCallback(async (): Promise<Branch[]> => {
    if (!userContext) return [];

    try {
      let restaurantId: string | null = null;

      if (isStaff && staffSession) {
        // For staff users, use the restaurant_id from staff session
        restaurantId = staffSession.restaurant_id;
      } else if (user) {
        // For owners, fetch the restaurant based on user ID
        const { data: restaurantData, error: restaurantError } = await supabase
          .from('restaurants')
          .select('id')
          .eq('owner_id', user.id)
          .single();

        if (restaurantError) {
          if (restaurantError.code === 'PGRST116') {
            return [];
          } else {
            throw new Error(`Failed to fetch restaurant: ${restaurantError.message}`);
          }
        }

        restaurantId = restaurantData?.id || null;
      }

      if (!restaurantId) {
        return [];
      }

      // Fetch branches for the restaurant
      const { data: branchesData, error: branchesError } = await supabase
        .from('branches')
        .select('*, restaurants(*)')
        .eq('restaurant_id', restaurantId);

      if (branchesError) {
        throw new Error(`Failed to fetch branches: ${branchesError.message}`);
      }

      return (branchesData as unknown as Branch[]) || [];
    } catch (err) {
      if (err instanceof Error) {
        throw err;
      } else {
        throw new Error('An unknown error occurred');
      }
    }
  }, [user, isStaff, staffSession, userContext]);

  // Use cached data hook - DISABLED when offline to prevent network errors
  const {
    data: branches,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate: invalidateCachedData
  } = useCachedData<Branch[]>(
    cacheKey,
    fetchBranches,
    {
      enabled: !!userContext && !shouldUseOffline, // Disable when offline
      ttl: TTL_CONFIG.branches,
      fallbackToCache: true,
      backgroundRefresh: false // Branches don't change often
    }
  );

  // Get restaurant ID for offline fetch (works for both staff and owners)
  const restaurantIdForOffline = useMemo(() => {
    // Staff users have restaurant_id in session
    if (staffSession?.restaurant_id) {
      return staffSession.restaurant_id;
    }
    // For owners, we need to get it from local session or cached data
    // The branches from cache may have restaurant_id
    if (branches && branches.length > 0) {
      return branches[0].restaurant_id;
    }
    return null;
  }, [staffSession, branches]);

  // Fetch branches from SQLite when offline
  useEffect(() => {
    if (!shouldUseOffline || !database) {
      return;
    }
    
    const fetchOfflineBranches = async () => {
      setOfflineLoading(true);
      try {
        // Try to get branches using restaurant ID if available
        let data: unknown[] = [];
        
        if (restaurantIdForOffline) {
          data = await database.getBranches(restaurantIdForOffline);
        } else {
          // Fallback: try to get all cached branches from local_session
          // This handles edge cases where restaurant_id isn't available
          console.warn('[useFilteredBranches] No restaurant ID for offline fetch, branches may be empty');
        }
        
        const branchData = (data || []).map((row: Record<string, unknown>) => ({
          id: row.id as string,
          restaurant_id: row.restaurant_id as string,
          name: row.name as string,
          address: row.address as string | null,
          phone: row.phone as string | null,
          tax_rate: row.tax_rate as number,
          service_charge_rate: row.service_charge_rate as number,
          currency: row.currency as string || 'USD',
          is_active: row.is_active === 1,
          created_at: row.created_at as string,
          updated_at: row.updated_at as string,
        })) as Branch[];
        setOfflineBranches(branchData);
      } catch (err) {
        console.error('[useFilteredBranches] Error fetching offline branches:', err);
      } finally {
        setOfflineLoading(false);
      }
    };
    
    fetchOfflineBranches();
  }, [shouldUseOffline, database, restaurantIdForOffline]);

  // Determine which branches to use
  const effectiveBranches = shouldUseOffline ? offlineBranches : branches;
  const effectiveLoading = shouldUseOffline ? offlineLoading : loading;

  // Apply role-based filtering to cached data
  const filteredBranches = useMemo(() => {
    if (!effectiveBranches) return [];
    
    // If not staff, return all branches (owner has full access)
    if (!isStaff || !staffSession) {
      return effectiveBranches;
    }

    // If staff has no branch restrictions (empty array), return all branches
    if (!staffSession.allowed_branches || staffSession.allowed_branches.length === 0) {
      return effectiveBranches;
    }

    // Filter branches based on allowed_branches
    return effectiveBranches.filter(branch => 
      staffSession.allowed_branches.includes(branch.id)
    );
  }, [effectiveBranches, isStaff, staffSession]);

  return {
    branches: filteredBranches,
    loading: effectiveLoading,
    error,
    isFromCache: shouldUseOffline ? false : isFromCache,
    refetch,
    invalidate: invalidateCachedData,
    hasAccess: (branchId: string) => {
      if (!isStaff || !staffSession) return true;
      if (!staffSession.allowed_branches || staffSession.allowed_branches.length === 0) return true;
      return staffSession.allowed_branches.includes(branchId);
    }
  };
}; 